<script lang="ts">
	import type { AnalysisResult } from '#lib/report/engine';
	import type { ExtractedDocument, Issue } from '#lib/types';
	import { t, i18n } from '#lib/i18n/state.svelte';
	import { issueTextEn } from '#lib/i18n/issues';
	import { toCsv, toHtml, toJson, downloadFile } from '#lib/report/export';
	import { toMarkdown } from '#lib/report/markdown';
	import { copyText } from '#lib/ui/clipboard';
	import { scoreTone, formatCount } from '#lib/ui/labels';
	import ScoreDial from './ScoreDial.svelte';
	import RevisionLetter from './RevisionLetter.svelte';
	import BadgePanel from './BadgePanel.svelte';
	import LangToggle from './LangToggle.svelte';
	import OverviewTab from './OverviewTab.svelte';
	import FindingsTab from './FindingsTab.svelte';
	import StatsTab from './StatsTab.svelte';
	import ReferencesTab from './ReferencesTab.svelte';
	import StructureTab from './StructureTab.svelte';
	import AccessTab from './AccessTab.svelte';
	import DocumentViewer from './DocumentViewer.svelte';
	import Methodology from './Methodology.svelte';
	import HistorySelector from './HistorySelector.svelte';
	import DiffView from './DiffView.svelte';
	import { loadHistory } from '#lib/history/storage';
	import { diffSnapshots } from '#lib/history/diff';
	import type { HistorySnapshot, SnapshotDiff } from '#lib/history/types';
	import { getJournal } from '#lib/journals';
	import { evaluateJournal } from '#lib/journals/evaluate';
	import type { JournalEvaluation } from '#lib/journals/types';

	interface Props {
		analysis: AnalysisResult;
		doc: ExtractedDocument;
		onreset: () => void;
		isDemo?: boolean;
		snapshot?: HistorySnapshot | null;
		journalId?: string | null;
	}

	let { analysis, doc, onreset, isDemo = false, snapshot = null, journalId = null }: Props = $props();
	const report = $derived(analysis.report);
	const journal = $derived(getJournal(journalId));
	const journalEvaluation: JournalEvaluation | null = $derived(
		journal ? evaluateJournal(journal, report, doc.text) : null
	);

	type TabKey =
		| 'overview'
		| 'findings'
		| 'stats'
		| 'references'
		| 'structure'
		| 'access'
		| 'method';
	let tab = $state<TabKey>('overview');
	let expandedId = $state<string | null>(null);
	let focusId = $state<string | null>(null);
	let viewerOpen = $state(true);
	let historyOpen = $state(false);
	let historyList = $state<HistorySnapshot[]>([]);
	let diff = $state<SnapshotDiff | null>(null);
	let copied = $state(false);
	let letterOpen = $state(false);
	let badgeOpen = $state(false);

	function openHistory() {
		if (!snapshot) return;
		historyList = loadHistory().filter((s) => s.id !== snapshot.id);
		historyOpen = true;
	}

	function selectHistory(selected: HistorySnapshot) {
		if (!snapshot) return;
		diff = diffSnapshots(selected, snapshot);
		historyOpen = false;
	}

	const tabs = $derived<{ key: TabKey; label: string; count?: number }[]>([
		{ key: 'overview', label: t('report.tab.overview') },
		{ key: 'findings', label: t('report.tab.findings'), count: report.issues.length },
		{ key: 'stats', label: t('report.tab.stats'), count: report.stats.summary.total },
		{ key: 'references', label: t('report.tab.refs'), count: report.citations.summary.citations },
		{
			key: 'structure',
			label: t('report.tab.structure'),
			count: report.structure.summary.tables + report.structure.summary.figures
		},
		{
			key: 'access',
			label: t('report.tab.access'),
			count: report.issues.filter((i) => i.category === 'access').length
		},
		{ key: 'method', label: t('report.tab.method') }
	]);

	function jump(issue: Issue) {
		focusId = null;
		viewerOpen = true;
		// Aynı issue'ya tekrar atlanabilsin diye önce temizle, sonra ata
		requestAnimationFrame(() => {
			focusId = issue.id;
		});
	}

	function jumpById(id: string) {
		const issue = report.issues.find((i) => i.id === id);
		if (issue) jump(issue);
	}

	function selectIssue(id: string) {
		expandedId = expandedId === id ? null : id;
		if (expandedId) {
			tab = 'findings';
			requestAnimationFrame(() => {
				const el = document.getElementById(`issue-${id}`);
				el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
			});
		}
	}

	/** Sağ paneldeki vurgudan gelir: her zaman aç, bulguya kaydır ve pulse göster. */
	function selectFromViewer(id: string) {
		expandedId = id;
		tab = 'findings';
		requestAnimationFrame(() => {
			const el = document.getElementById(`issue-${id}`);
			if (!el) return;
			el.scrollIntoView({ behavior: 'smooth', block: 'center' });
			el.classList.add('pulsing');
			setTimeout(() => el.classList.remove('pulsing'), 1500);
		});
	}

	function exportJson() {
		downloadFile(toJson(report), baseName() + '.json', 'application/json');
	}
	function exportHtml() {
		downloadFile(toHtml(report), baseName() + '.html', 'text/html');
	}
	function exportCsv() {
		downloadFile(toCsv(report), baseName() + '.csv', 'text/csv');
	}
	function baseName() {
		return report.document.fileName.replace(/\.[^.]+$/, '') + '-repcheck';
	}

	function printReport() {
		window.print();
	}

	async function copyMarkdown() {
		const ok = await copyText(toMarkdown(report));
		if (ok) {
			copied = true;
			setTimeout(() => (copied = false), 1800);
		}
	}

	function printTitle(issue: Issue): string {
		const en = i18n.lang === 'en' ? issueTextEn(issue) : null;
		return en?.title ?? issue.title;
	}

	function printDetail(issue: Issue): string {
		const en = i18n.lang === 'en' ? issueTextEn(issue) : null;
		return en?.detail ?? issue.detail;
	}

	const printDate = $derived(
		new Date(report.generatedAt).toLocaleDateString(i18n.lang === 'tr' ? 'tr-TR' : 'en-US', {
			year: 'numeric',
			month: 'long',
			day: 'numeric'
		})
	);

	const qualityLabel = $derived(
		report.document.quality.level === 'high'
			? t('report.qualityHigh')
			: report.document.quality.level === 'medium'
				? t('report.qualityMedium')
				: t('report.qualityLow')
	);
</script>

<div class="report">
	<header class="head">
		<div class="head-left">
			{#if isDemo}
				<button class="btn btn-primary btn-sm back" onclick={onreset}>{t('demo.ownCta')}</button>
			{:else}
				<button class="btn btn-ghost btn-sm back" onclick={onreset}>← {t('report.back')}</button>
			{/if}
			<div class="file-meta">
				<h1>{report.document.fileName}</h1>
				<p class="sub">
					<span class="chip chip-neutral">{report.document.format.toUpperCase()}</span>
					{#if report.document.pages}
						<span>{t('viewer.pages', { n: report.document.pages })}</span>
					{/if}
					<span>{t('report.charsK', { n: Math.round(report.document.chars / 1000) })}</span>
					<span class="dot-sep">·</span>
					<span style:color={qualityLabel === t('report.qualityLow') ? 'var(--critical)' : 'inherit'}>
						{t('report.quality', { level: qualityLabel })}
					</span>
				</p>
			</div>
		</div>

		<div class="head-right">
			<button class="btn btn-sm compare" onclick={openHistory} disabled={!snapshot}>
				{t('report.compare')}
			</button>
			<LangToggle compact />
			<div class="mini-score" title={t('overview.eyebrow')}>
				<span class="ms-num" style:color={scoreTone(report.scores.overall)}>{report.scores.overall}</span>
				<span class="ms-grade" style:color={scoreTone(report.scores.overall)}>{report.scores.grade}</span>
			</div>
			<div class="exports">
				<button class="btn btn-sm" onclick={printReport} title={t('report.download')} aria-label={t('report.download')}>
					{t('report.pdf')}
				</button>
				<button class="btn btn-sm" onclick={copyMarkdown} title={t('report.markdown')} aria-label={t('report.markdown')}>
					{copied ? t('report.copied') : t('report.md')}
				</button>
				<button class="btn btn-sm" onclick={() => (letterOpen = true)} title={t('revision.title')} aria-label={t('revision.title')}>
					{t('revision.short')}
				</button>
				<button class="btn btn-sm" onclick={() => (badgeOpen = true)} title={t('badge.title')} aria-label={t('badge.title')}>
					{t('badge.short')}
				</button>
				<button class="btn btn-sm" onclick={exportHtml}>{t('report.exportHtml')}</button>
				<button class="btn btn-sm" onclick={exportCsv}>CSV</button>
				<button class="btn btn-sm" onclick={exportJson}>JSON</button>
			</div>
		</div>
	</header>

	<nav class="tabs" aria-label={t('report.tabsAria')}>
		{#each tabs as tabItem (tabItem.key)}
			<button
				class="tab"
				class:active={tab === tabItem.key}
				onclick={() => (tab = tabItem.key)}
				aria-current={tab === tabItem.key ? 'page' : undefined}
			>
				{tabItem.label}
				{#if tabItem.count !== undefined}<span class="tab-count">{formatCount(tabItem.count)}</span>{/if}
			</button>
		{/each}
		<button class="tab viewer-toggle" class:active={viewerOpen} onclick={() => (viewerOpen = !viewerOpen)}>
			{viewerOpen ? t('report.hideText') : t('report.showText')}
		</button>
	</nav>

	<div class="body" class:with-viewer={viewerOpen}>
		<main class="main">
			{#if tab === 'overview'}
				<OverviewTab {report} onjump={jumpById} onselect={selectIssue} />
			{:else if tab === 'findings'}
				<FindingsTab {report} onjump={jump} onselect={selectIssue} {expandedId} />
			{:else if tab === 'stats'}
				<StatsTab {report} onjump={jump} />
			{:else if tab === 'references'}
				<ReferencesTab {report} onjump={jump} />
			{:else if tab === 'structure'}
				<StructureTab {report} onjump={jump} journal={journalEvaluation} />
			{:else if tab === 'access'}
				<AccessTab {report} onjump={jump} />
			{:else}
				<Methodology {report} {doc} />
			{/if}
		</main>

		{#if viewerOpen}
			<aside class="aside" aria-label="Belge metni">
				<DocumentViewer {doc} issues={report.issues} {focusId} onissuetap={selectFromViewer} />
			</aside>
		{/if}
	</div>
</div>

{#if historyOpen && snapshot}
	<HistorySelector
		snapshots={historyList}
		onselect={selectHistory}
		onclose={() => (historyOpen = false)}
	/>
{/if}

{#if diff}
	<DiffView {diff} onclose={() => (diff = null)} />
{/if}

{#if letterOpen}
	<RevisionLetter {report} onclose={() => (letterOpen = false)} />
{/if}

{#if badgeOpen}
	<BadgePanel {report} onclose={() => (badgeOpen = false)} />
{/if}

<div class="print-report">
	<header class="print-head">
		<p class="print-brand">RepCheck</p>
		<h1>{report.document.fileName}</h1>
		<p class="print-meta">{printDate} · {t('print.score')}: {report.scores.overall}/{report.scores.grade}</p>
	</header>

	<div class="print-score-row">
		<ScoreDial score={report.scores.overall} grade={report.scores.grade} size={118} label={t('print.score')} />
		<div class="print-counts">
			<span>{t('severity.critical')}: {report.counts.critical}</span>
			<span>{t('severity.warning')}: {report.counts.warning}</span>
			<span>{t('severity.info')}: {report.counts.info}</span>
		</div>
	</div>

	<h2>{t('print.categories')}</h2>
	<table class="print-table">
		<thead>
			<tr>
				<th>{t('print.category')}</th>
				<th>{t('print.score')}</th>
				<th>{t('print.weight')}</th>
			</tr>
		</thead>
		<tbody>
			{#each report.scores.categories as cat (cat.category)}
				<tr>
					<td>{t(`category.${cat.category}`)}</td>
					<td class="num">{cat.score}</td>
					<td class="num">{Math.round(cat.weight * 100)}%</td>
				</tr>
			{/each}
		</tbody>
	</table>

	<h2>{t('print.issues')} ({report.issues.length})</h2>
	{#each report.scores.categories as cat (cat.category)}
		{@const list = report.issues.filter((i) => i.category === cat.category)}
		{#if list.length > 0}
			<section class="print-issues">
				<h3>{t(`category.${cat.category}`)} ({list.length})</h3>
				{#each list as issue (issue.id)}
					<article class="print-issue">
						<h4>
							<span class="print-sev">{t(`severity.${issue.severity}`)}</span>
							{printTitle(issue)}
						</h4>
						<p>{printDetail(issue)}</p>
						{#if issue.location.page !== undefined}
							<p class="print-loc">{t('common.page', { n: issue.location.page })}</p>
						{:else if issue.location.sourceLine !== undefined}
							<p class="print-loc">{t('common.line', { n: issue.location.sourceLine })}</p>
						{/if}
					</article>
				{/each}
			</section>
		{/if}
	{/each}

	<footer class="print-footer">{t('print.footer')}</footer>
</div>

<style>
	.report {
		display: flex;
		flex-direction: column;
		min-height: 100vh;
	}
	.head {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 1rem;
		padding: 0.9rem clamp(1rem, 3vw, 2rem);
		border-bottom: 1px solid var(--line);
		background: rgb(250 248 244 / 0.92);
		backdrop-filter: blur(8px);
		flex-wrap: wrap;
		position: sticky;
		top: 0;
		z-index: 20;
	}
	.head-left {
		display: flex;
		align-items: center;
		gap: 0.9rem;
		min-width: 0;
	}
	.back {
		flex: none;
	}
	.file-meta {
		min-width: 0;
	}
	h1 {
		font-family: var(--font-display);
		font-size: clamp(1.15rem, 2.2vw, 1.5rem);
		margin: 0;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		max-width: 60ch;
	}
	.sub {
		display: flex;
		align-items: center;
		gap: 0.45rem;
		flex-wrap: wrap;
		margin: 0.15rem 0 0;
		font-size: 0.75rem;
		color: var(--muted);
	}
	.dot-sep {
		color: var(--line-strong);
	}
	.head-right {
		display: flex;
		align-items: center;
		gap: 0.8rem;
		flex-wrap: wrap;
		justify-content: flex-end;
	}
	.mini-score {
		display: flex;
		align-items: baseline;
		gap: 0.25rem;
		font-family: var(--font-display);
		line-height: 1;
	}
	.ms-num {
		font-size: 1.7rem;
	}
	.ms-grade {
		font-size: 0.95rem;
		font-weight: 600;
	}
	.exports {
		display: flex;
		gap: 0.35rem;
		flex-wrap: wrap;
		justify-content: flex-end;
	}
	.tabs {
		display: flex;
		gap: 0.15rem;
		padding: 0 clamp(1rem, 3vw, 2rem);
		border-bottom: 1px solid var(--line);
		overflow-x: auto;
		-webkit-overflow-scrolling: touch;
		scrollbar-width: thin;
		background: var(--paper);
	}
	.tab {
		border: 0;
		background: transparent;
		padding: 0.7rem 0.8rem;
		min-height: 44px;
		font-size: 0.86rem;
		color: var(--muted);
		cursor: pointer;
		border-bottom: 2px solid transparent;
		white-space: nowrap;
		display: inline-flex;
		align-items: center;
		gap: 0.35rem;
	}
	.tab:hover {
		color: var(--ink);
	}
	.tab.active {
		color: var(--ink);
		font-weight: 600;
		border-bottom-color: var(--ink);
	}
	.tab-count {
		font-size: 0.7rem;
		background: var(--paper-2);
		border: 1px solid var(--line);
		border-radius: 99px;
		padding: 0 0.35rem;
		color: var(--muted);
		font-variant-numeric: tabular-nums;
	}
	.viewer-toggle {
		margin-left: auto;
		font-size: 0.78rem;
	}
	.body {
		display: grid;
		grid-template-columns: 1fr;
		gap: 1.25rem;
		padding: 1.25rem clamp(1rem, 3vw, 2rem) 3rem;
		align-items: start;
	}
	.body.with-viewer {
		grid-template-columns: minmax(0, 1fr) minmax(380px, 45%);
	}
	@media (max-width: 1080px) {
		.body.with-viewer {
			grid-template-columns: 1fr;
		}
	}
	.main {
		min-width: 0;
	}
	.aside {
		position: sticky;
		top: 125px;
		min-width: 0;
		animation: fade-up 0.4s var(--ease) both;
	}
	@media (max-width: 1080px) {
		.aside {
			position: static;
		}
	}

	@media (max-width: 640px) {
		.head {
			padding: 0.65rem 0.9rem;
			gap: 0.5rem;
		}
		.head-left {
			flex-wrap: wrap;
			gap: 0.35rem 0.6rem;
			width: 100%;
		}
		.back {
			min-height: 44px;
			padding-inline: 0.7rem;
			font-size: 0.8rem;
		}
		.file-meta {
			flex: 1 1 100%;
			min-width: 0;
		}
		h1 {
			white-space: normal;
			font-size: 1.02rem;
			max-width: 100%;
		}
		.head-right {
			width: 100%;
			flex-wrap: wrap;
			gap: 0.4rem 0.6rem;
			justify-content: space-between;
		}
		.exports {
			gap: 0.25rem;
			flex-wrap: wrap;
			justify-content: flex-end;
		}
		.exports .btn {
			padding: 0.35rem 0.55rem;
			font-size: 0.76rem;
			min-height: 44px;
		}
		.ms-num {
			font-size: 1.4rem;
		}
		.tabs {
			position: static;
		}
		.body {
			padding: 1rem 0.75rem 2.5rem;
			gap: 0.9rem;
		}
	}

	@media (max-width: 720px) {
		/* Mobilde belge görüntüleyici gizlenir; masaüstünde yan panel olarak kalır. */
		.aside,
		.viewer-toggle {
			display: none;
		}
	}

	.print-report {
		display: none;
	}

	@media print {
		:global(body) {
			background: #fff;
		}
		.report {
			display: none !important;
		}
		:global(.overlay) {
			display: none !important;
		}
		.print-report {
			display: block;
			color: #1c1917;
			font-size: 10.5pt;
			line-height: 1.45;
		}
		.print-head {
			border-bottom: 2px solid #1c1917;
			padding-bottom: 8pt;
			margin-bottom: 12pt;
		}
		.print-brand {
			margin: 0 0 2pt;
			font-size: 9pt;
			letter-spacing: 0.12em;
			text-transform: uppercase;
			font-weight: 700;
		}
		.print-head h1 {
			font-family: Georgia, 'Times New Roman', serif;
			font-size: 18pt;
			margin: 0 0 3pt;
			overflow-wrap: anywhere;
		}
		.print-meta {
			margin: 0;
			color: #57534e;
			font-size: 9.5pt;
		}
		.print-score-row {
			display: flex;
			align-items: center;
			gap: 20pt;
			margin: 4pt 0 12pt;
		}
		.print-counts {
			display: flex;
			flex-direction: column;
			gap: 2pt;
			font-size: 9.5pt;
		}
		.print-table {
			width: 100%;
			border-collapse: collapse;
			font-size: 9.5pt;
			margin-bottom: 10pt;
		}
		.print-table th,
		.print-table td {
			text-align: left;
			padding: 3pt 4pt;
			border-bottom: 1px solid #d6d3d1;
		}
		.print-table th {
			font-size: 8pt;
			text-transform: uppercase;
			letter-spacing: 0.05em;
			color: #57534e;
		}
		.print-table .num {
			text-align: right;
		}
		.print-report h2 {
			font-size: 11pt;
			margin: 14pt 0 5pt;
			break-after: avoid;
		}
		.print-issues h3 {
			font-size: 9.5pt;
			margin: 8pt 0 4pt;
			break-after: avoid;
		}
		.print-issue {
			break-inside: avoid;
			margin-bottom: 6pt;
			padding-left: 8pt;
			border-left: 1.5pt solid #d6d3d1;
		}
		.print-issue h4 {
			font-size: 9.5pt;
			margin: 0 0 1pt;
		}
		.print-sev {
			text-transform: uppercase;
			font-size: 7.5pt;
			letter-spacing: 0.06em;
			color: #57534e;
			margin-right: 4pt;
		}
		.print-issue p {
			margin: 0;
			color: #44403c;
			font-size: 9pt;
		}
		.print-loc {
			color: #78716c !important;
			font-size: 8pt !important;
		}
		.print-footer {
			margin-top: 14pt;
			padding-top: 6pt;
			border-top: 1px solid #d6d3d1;
			font-size: 8pt;
			color: #78716c;
		}
	}

	@page {
		margin: 14mm;
	}
</style>
